import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo } from "react";

import { fetchCardData } from "checkout-query-client/api/fetch-card-data";
import { ExperienceType } from "checkout-query-client/api/types";
import { PaymentType } from "checkout-query-client/types/form-types";

import { GetBinDataResponse } from "frontend-client";

export interface CardNetworksApiLookupProps {
  apiHost?: string;
  checkoutSessionToken?: string;
  cardNumber: string;
  experienceType?: ExperienceType;
  encryptionKey?: string;
  target?: string;
  isFieldLengthValidForLookup?: boolean;
  merchantCobrandedCardsEnabled?: boolean;
  paymentType?: PaymentType;
  version: string;
}

type CardNetworksApiLookupQueryKey = Array<
  CardNetworksApiLookupProps & { scope: "card-networks-api-lookup" }
>;

let cardNetworksApiLookupQueryKey: CardNetworksApiLookupQueryKey = [
  { scope: "card-networks-api-lookup", version: "", cardNumber: "" },
];

let isCreditCardQueryEnabled = false;

export const useCardNetworksApiLookup = ({
  apiHost,
  checkoutSessionToken,
  cardNumber,
  experienceType,
  encryptionKey,
  isFieldLengthValidForLookup = false,
  merchantCobrandedCardsEnabled = false,
  paymentType,
  version,
}: CardNetworksApiLookupProps) => {
  isCreditCardQueryEnabled =
    !!apiHost &&
    !!checkoutSessionToken &&
    !!cardNumber &&
    !!experienceType &&
    !!encryptionKey &&
    cardNumber !== "" &&
    merchantCobrandedCardsEnabled &&
    isFieldLengthValidForLookup &&
    paymentType === PaymentType.creditCard;

  // Debounce card number only once it is a valid field
  const debouncedCardNumber = useDebounce(
    cardNumber,
    isCreditCardQueryEnabled ? 100 : 0
  );

  cardNetworksApiLookupQueryKey = useMemo(
    () =>
      [
        {
          scope: "card-networks-api-lookup",
          apiHost,
          checkoutSessionToken,
          cardNumber: debouncedCardNumber,
          experienceType,
          encryptionKey,
          version: "",
        },
      ] as CardNetworksApiLookupQueryKey,
    [
      apiHost,
      checkoutSessionToken,
      debouncedCardNumber,
      experienceType,
      encryptionKey,
      version,
    ]
  );

  return useQuery({
    queryKey: cardNetworksApiLookupQueryKey,
    queryFn: fetchCardData,
    select: (data: GetBinDataResponse) => {
      // TODO: CB: query still runs even though isFieldLengthValidForLookup is false.. need to investigate
      if (!isCreditCardQueryEnabled) {
        // temporary bypass (see above TODO)
        return [];
      }
      return data.cardNetworks;
    },
    enabled: isCreditCardQueryEnabled,
  });
};
